// NPM MODULES
import React, { useState, useEffect, useRef } from 'react';
import {
  InputLabel,
  Box,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Stack,
} from '@mui/material';

// COMPONENTS
import GlobalBlackListItem from './GlobalBlackListItem';
import GlobalBlackListSingle from './GlobalBlackListSingle';
import Paginator from '../../../common/Paginator';

// CONFIG
import config from '../../../../Config';

// FORMATTED MESSAGES
import AddressBookFormattedMessages from '../../../../messages/websms/AddressBookFormattedMessages';

// NETWORKING
import useAddressBookRequest from '../../../../requests/AddressBookRequest';
import Loader from '../../../common/Loader';
import CustomModal from '../../../common/CustomModal';

const GlobalBlacklist = (props) => {
  const paginatorRef = useRef(null);
  const [pageLength, setPageLength] = useState(config.defaultPL ?? 10);
  const [openModal, setOpenModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [contactSearch, setContactSearch] = useState('');
  const [blacklistItems, setBlacklistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlacklist, setSelectedBlacklist] = useState(null);
  const { deleteBlackList, getBlacklistItems } = useAddressBookRequest();

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    const { list } = await getBlacklistItems(props.errorHandler);
    console.log('black list data:', list);
    setBlacklistItems(list);
    setLoading(false);
  };

  /*
    Passes the list item back
  */
  const editListItem = (element) => {
    setSelectedBlacklist(element);
    setOpenModal(true);
    setModalType('addEdit');
  };

  /*
    Handles the change event when clicking a pagination number
  */
  const changeEvent = (value) => {
    setPageNumber(value);
  };

  const changePageLength = (e) => {
    const value = e.target.value;
    setPageLength(value);
    setPageNumber(1);
  };

  /*
    Handles changes in the form
  */
  const handleContactSearchChange = async (event) => {
    setPageNumber(1);
    setContactSearch(event.target.value);

    // Resets page using useRef hook
    if (paginatorRef?.current) paginatorRef.current.changePage();
  };

  const handleCloseModal = (refreshData) => {
    setSelectedBlacklist(null);
    setOpenModal(false);
    setModalType(null);
    if (refreshData) getData();
  };

  const handleSearchFilter = () => {
    if (contactSearch) {
      return blacklistItems.filter((contact) => {
        return `${contact.number} ${contact.email}`
          .toLowerCase()
          .includes(contactSearch.toLowerCase());
      });
    }
    return blacklistItems;
  };

  const generateButtons = () => {
    return (
      <Stack sx={{ marginTop: 4, marginBottom: 1, gap: 1 }}>
        <Stack
          sx={{
            flexDirection: { lg: 'row' },
            justifyContent: 'space-between',
            alignItems: { lg: 'end' },
            gap: 2,
          }}
        >
          {accessLevel === '3' && accessType === '3' && (
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setSelectedBlacklist(null);
                setModalType('addEdit');
                setOpenModal(true);
              }}
              sx={{ marginTop: 0.25 }}
            >
              {AddressBookFormattedMessages.createNewButton}
            </Button>
          )}
        </Stack>
      </Stack>
    );
  };

  const generateTable = () => {
    if (loading) return <Loader />;
    if (!blacklistItems || !blacklistItems.length)
      return 'No blacklist entries found.';

    return (
      <Box>
        <TableContainer
          sx={{ marginBottom: 4, overflowX: 'auto', width: '100%' }}
        >
          <Table variant="rounded">
            <TableHead>
              <TableRow key="head">
                <TableCell>EntryId</TableCell>
                <TableCell>Number</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{generateTableRows()}</TableBody>
          </Table>
        </TableContainer>

        <Paginator
          itemCount={blacklistItems.length}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          pageLength={pageLength}
          changeEvent={changeEvent}
          editFunction={(e) => changePageLength(e)}
          showTotal={'Found'}
          toolTip={'Maximum of 4,000 records shown'}
        />
      </Box>
    );
  };

  /*
    Generate the table for Contacts
  */
  const generateTableRows = () => {
    const { accessLevel, accessType } = props;
    const isGodAdmin = accessLevel === '3' && accessType === '3';

    const filteredBlacklistItems = handleSearchFilter();

    const displayList = filteredBlacklistItems.slice(
      (pageNumber - 1) * pageLength,
      pageNumber * pageLength
    );
    return displayList.map((item, i) => (
      <GlobalBlackListItem
        isGodAdmin={isGodAdmin}
        onClick={() => editListItem(item)}
        key={i}
        entryId={item.blacklist_id}
        number={item.number}
        date={new Date(item.updated_at).toString()}
        onDelete={() => {
          setSelectedBlacklist(item);
          setOpenModal(true);
          setModalType('delete');
        }}
      />
    ));
  };

  const deleteBlacklistItem = async (entryId) => {
    setLoading(true);
    await deleteBlackList(entryId, props.errorHandler);
    const updatedBlacklistItems = blacklistItems.filter(
      (item) => item.blacklist_id !== entryId
    );
    setBlacklistItems(updatedBlacklistItems);
    setLoading(false);
    handleCloseModal();
  };

  const { accessLevel, accessType } = props;

  return (
    <React.Fragment>
      <Box
        sx={{
          backgroundColor: 'common.white',
          borderRadius: '0 0 20px 20px',
          paddingX: 4,
          paddingY: 2,
          maxWidth: 800,
          marginBottom: 4,
        }}
      >
        <Stack
          sx={{
            flexDirection: { md: 'row' },
            justifyContent: 'space-between',
            alignItems: { md: 'end' },
            gap: 2,
          }}
        >
          <Stack sx={{ flex: 1 }}>
            <InputLabel>
              {AddressBookFormattedMessages.searchByNumber}
            </InputLabel>

            <TextField
              id="contactSearch"
              autoFocus
              type="text"
              defaultValue=""
              onChange={handleContactSearchChange}
              fullWidth
              variant="outlined"
              size="small"
            />
          </Stack>

          <Stack sx={{ flexDirection: 'row', gap: 2 }}>
            <Button
              variant="outlined"
              color="secondary"
              name="submit"
              onClick={handleContactSearchChange}
            >
              Search
            </Button>
          </Stack>
        </Stack>
      </Box>
      {generateButtons()}
      {generateTable()}

      <CustomModal
        open={openModal}
        setOpen={setOpenModal}
        title={
          modalType == 'delete'
            ? 'Delete Blacklist Entry'
            : selectedBlacklist
            ? 'Edit Blacklist Entry'
            : 'New Blacklist Entry'
        }
        width={500}
        icon="contact"
      >
        {modalType == 'addEdit' && (
          <GlobalBlackListSingle
            backFunction={handleCloseModal}
            selected={selectedBlacklist ?? null}
            errorHandler={props.errorHandler}
          />
        )}

        {modalType == 'delete' && (
          <Box>
            <Typography gutterBottom>
              {AddressBookFormattedMessages.deleteBlackListItems}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'end',
              }}
            >
              <Button
                onClick={() => handleCloseModal(false)}
                variant="link"
                color="secondary"
              >
                {AddressBookFormattedMessages.cancelText}
              </Button>

              <Button
                onClick={() =>
                  deleteBlacklistItem(selectedBlacklist.blacklist_id)
                }
                variant="contained"
                color="secondary"
              >
                {AddressBookFormattedMessages.deleteText}
              </Button>
            </Box>
          </Box>
        )}
      </CustomModal>
    </React.Fragment>
  );
};

export default GlobalBlacklist;
